import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import moment from "moment";

import {
  Box,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Chip,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddIcon from "@mui/icons-material/Add";
import DescriptionIcon from "@mui/icons-material/Description";

import Button from "src/components/core/Button";
import device from "src/styles/device";
import useDeviceSize from "src/hooks/useDeviceSize";
import { getCertificatesList } from "src/api/certificate";
import Main from "src/components/core/Main";

// Styled Components
const DivMuiContainer = styled.div`
  width: 100%;
  height: auto;
  padding: 24px;
  border-radius: 32px;
  background-color: #ffffff;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  justify-content: center;
  align-items: center;

  @media ${device.tabletL} {
    padding: 16px;
    border-radius: 16px;
  }

  @media ${device.mobileL} {
    padding: 12px;
    border-radius: 12px;
  }
`;

const DivMuiInside = styled.div`
  width: 100%;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: ${({ mt }) => (mt ? `${mt}px` : "0px")};

  @media ${device.tabletL} {
    flex-direction: column;
    gap: 12px;
  }
`;

const WrapperSearch = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  justify-content: center;
  margin: 0;

  .TextField-without-border-radius fieldset {
    border-radius: 16px;
  }

  @media ${device.mobileL} {
    width: 100%;
  }
`;

const WrapperButton = styled.div`
  display: flex;
  gap: 12px;

  @media ${device.tabletL} {
    width: 100%;
    justify-content: stretch;
  }
`;

const WrapperDataGrid = styled.div`
  height: 600px;
  width: 100%;

  .MuiDataGrid-root {
    border: 1px solid #ffffff;
    border-radius: 16px;
  }

  .MuiDataGrid-columnHeaders {
    background-color: #f5f5f5;
  }

  .MuiDataGrid-cell {
    border-bottom: 1px solid #f0f0f0;
  }

  .MuiDataGrid-row:hover {
    background-color: #f9f9f9;
  }

  @media ${device.mobileL} {
    height: 500px;
  }
`;

const CertificateImage = styled(Avatar)`
  width: 60px;
  height: 60px;
  border-radius: 8px;

  @media ${device.mobileL} {
    width: 40px;
    height: 40px;
  }
`;

const CertificatesList = ({ open }) => {
  const navigate = useNavigate();
  const [deviceWidth] = useDeviceSize();

  const [certificatesList, setCertificatesList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);

  const [dataSearch, setDataSearch] = useState({
    keyword: "",
    page: 0,
    pageSize: 25,
  });

  // Fetch certificates data
  const fetchCertificates = async () => {
    try {
      setIsLoading(true);
      const response = await getCertificatesList({
        keyword: dataSearch.keyword,
        page: dataSearch.page,
        pageSize: dataSearch.pageSize,
      });
      console.log("Fetched certificates:", response);
      setCertificatesList(response.items || []);
      setRowCount(response.total || 0);
    } catch (error) {
      console.error("Error fetching certificates:", error);

      // Fallback to mock data if API fails
      const mockData = [
        {
          id: "1",
          name: "ISO 9001:2015",
          image: "https://via.placeholder.com/300x200?text=ISO+9001",
          description:
            "มาตรฐานระบบการจัดการคุณภาพ เพื่อให้องค์กรสามารถดำเนินงานได้อย่างมีประสิทธิภาพ",
          status: "active",
          createdAt: new Date("2024-01-15"),
          updatedAt: new Date("2024-02-01"),
        },
        {
          id: "2",
          name: "ISO 14001:2015",
          image: "https://via.placeholder.com/300x200?text=ISO+14001",
          description:
            "มาตรฐานระบบการจัดการสิ่งแวดล้อม เพื่อการพัฒนาอย่างยั่งยืน",
          status: "active",
          createdAt: new Date("2024-01-20"),
          updatedAt: new Date("2024-01-25"),
        },
        {
          id: "3",
          name: "OHSAS 18001",
          image: "https://via.placeholder.com/300x200?text=OHSAS+18001",
          description: "มาตรฐานความปลอดภัยและอาชีวอนามัย ในสถานที่ทำงาน",
          status: "inactive",
          createdAt: new Date("2024-02-01"),
          updatedAt: new Date("2024-02-10"),
        },
        {
          id: "4",
          name: "CE Marking",
          image: "https://via.placeholder.com/300x200?text=CE+Mark",
          description: "เครื่องหมายรับรองผลิตภัณฑ์สำหรับตลาดยุโรป",
          status: "active",
          createdAt: new Date("2024-02-05"),
          updatedAt: new Date("2024-02-15"),
        },
        {
          id: "4",
          name: "CE Marking",
          image: "https://via.placeholder.com/300x200?text=CE+Mark",
          description: "เครื่องหมายรับรองผลิตภัณฑ์สำหรับตลาดยุโรป",
          status: "active",
          createdAt: new Date("2024-02-05"),
          updatedAt: new Date("2024-02-15"),
        },
        {
          id: "4",
          name: "CE Marking",
          image: "https://via.placeholder.com/300x200?text=CE+Mark",
          description: "เครื่องหมายรับรองผลิตภัณฑ์สำหรับตลาดยุโรป",
          status: "active",
          createdAt: new Date("2024-02-05"),
          updatedAt: new Date("2024-02-15"),
        },
        {
          id: "4",
          name: "CE Marking",
          image: "https://via.placeholder.com/300x200?text=CE+Mark",
          description: "เครื่องหมายรับรองผลิตภัณฑ์สำหรับตลาดยุโรป",
          status: "active",
          createdAt: new Date("2024-02-05"),
          updatedAt: new Date("2024-02-15"),
        },
        {
          id: "4",
          name: "CE Marking",
          image: "https://via.placeholder.com/300x200?text=CE+Mark",
          description: "เครื่องหมายรับรองผลิตภัณฑ์สำหรับตลาดยุโรป",
          status: "active",
          createdAt: new Date("2024-02-05"),
          updatedAt: new Date("2024-02-15"),
        },
        {
          id: "4",
          name: "CE Marking",
          image: "https://via.placeholder.com/300x200?text=CE+Mark",
          description: "เครื่องหมายรับรองผลิตภัณฑ์สำหรับตลาดยุโรป",
          status: "active",
          createdAt: new Date("2024-02-05"),
          updatedAt: new Date("2024-02-15"),
        },
        {
          id: "4",
          name: "CE Marking",
          image: "https://via.placeholder.com/300x200?text=CE+Mark",
          description: "เครื่องหมายรับรองผลิตภัณฑ์สำหรับตลาดยุโรป",
          status: "active",
          createdAt: new Date("2024-02-05"),
          updatedAt: new Date("2024-02-15"),
        },
        {
          id: "4",
          name: "CE Marking",
          image: "https://via.placeholder.com/300x200?text=CE+Mark",
          description: "เครื่องหมายรับรองผลิตภัณฑ์สำหรับตลาดยุโรป",
          status: "active",
          createdAt: new Date("2024-02-05"),
          updatedAt: new Date("2024-02-15"),
        },
        {
          id: "4",
          name: "CE Marking",
          image: "https://via.placeholder.com/300x200?text=CE+Mark",
          description: "เครื่องหมายรับรองผลิตภัณฑ์สำหรับตลาดยุโรป",
          status: "active",
          createdAt: new Date("2024-02-05"),
          updatedAt: new Date("2024-02-15"),
        },
      ];

      const filteredData = mockData.filter(
        (item) =>
          item.name.toLowerCase().includes(dataSearch.keyword.toLowerCase()) ||
          item.description
            .toLowerCase()
            .includes(dataSearch.keyword.toLowerCase()),
      );

      setCertificatesList(filteredData);
      setRowCount(filteredData.length);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSearch.keyword, dataSearch.page]);

  const onSearchButton = () => {
    // Trigger search
    setDataSearch((prev) => ({ ...prev }));
  };

  const handleView = (id) => {
    // Navigate to certificate detail page
    navigate(`/certificate/detail/${id}`);
  };

  const handleEdit = (id) => {
    // Navigate to certificate edit page
    navigate(`/certificate/edit/${id}`);
  };

  const handleAdd = () => {
    // Navigate to certificate create page
    navigate("/certificate/create");
  };

  // Desktop Columns
  const DesktopColumns = [
    { field: "id", headerName: "ID", width: 70, hide: true },
    {
      field: "image",
      headerName: "รูป",
      width: 100,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ py: 1 }}>
          <CertificateImage
            src={params.row.icon}
            alt={params.row.name}
            variant="rounded"
          >
            <DescriptionIcon />
          </CertificateImage>
        </Box>
      ),
    },
    {
      field: "name",
      headerName: "ชื่อใบรับรอง",
      flex: 1,
      minWidth: 200,
      editable: false,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: "#252525",
              lineHeight: 1.2,
            }}
          >
            {params.row.name}
          </Typography>
        </Box>
      ),
    },
    {
      field: "description",
      headerName: "คำอธิบาย",
      flex: 2,
      minWidth: 300,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: "#666666",
              lineHeight: 1.4,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {params.row.description}
          </Typography>
        </Box>
      ),
    },
    {
      field: "createdAt",
      headerName: "วันที่สร้าง",
      width: 140,
      editable: false,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" color="textSecondary">
            {moment(params.row.createdAt).format("DD/MM/YYYY")}
          </Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "การดำเนินการ",
      width: 120,
      disableColumnMenu: true,
      getActions: (params) => [
        <GridActionsCellItem
          key="view"
          icon={<VisibilityIcon />}
          label="ดู"
          onClick={() => handleView(params.id)}
          // color="primary"
        />,
        <GridActionsCellItem
          key="edit"
          icon={<EditRoundedIcon />}
          label="แก้ไข"
          onClick={() => handleEdit(params.id)}
          // color="primary"
        />,
      ],
    },
  ];

  // Mobile Columns
  const MobileColumns = [
    {
      field: "combined",
      headerName: "ใบรับรอง",
      flex: 1,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            py: 1,
            width: "100%",
          }}
        >
          <CertificateImage
            src={params.row.image}
            alt={params.row.name}
            variant="rounded"
          >
            <DescriptionIcon />
          </CertificateImage>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: "#252525",
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              {params.row.name}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: "#666666",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.3,
              }}
            >
              {params.row.description}
            </Typography>

            <Box sx={{ mt: 1, display: "flex", gap: 1, alignItems: "center" }}>
              <Chip
                label={params.row.status === "active" ? "ใช้งาน" : "ไม่ใช้งาน"}
                color={params.row.status === "active" ? "success" : "default"}
                size="small"
                variant="outlined"
              />

              <Typography variant="caption" color="textSecondary">
                {moment(params.row.createdAt).format("DD/MM/YY")}
              </Typography>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "",
      width: 80,
      disableColumnMenu: true,
      getActions: (params) => [
        <GridActionsCellItem
          key="view"
          icon={<VisibilityIcon />}
          label="ดู"
          onClick={() => handleView(params.id)}
          color="primary"
        />,
        <GridActionsCellItem
          key="edit"
          icon={<EditRoundedIcon />}
          label="แก้ไข"
          onClick={() => handleEdit(params.id)}
          color="primary"
        />,
      ],
    },
  ];

  return (
    <Main open={open}>
      <DivMuiContainer>
        {/* Search and Add Button */}
        <DivMuiInside mt={10}>
          <WrapperSearch>
            <TextField
              fullWidth
              placeholder="ค้นหาใบรับรองจากชื่อ หรือ รายละเอียด"
              className="TextField-without-border-radius"
              sx={{
                borderRadius: "16px",
              }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={onSearchButton} sx={{ padding: 0 }}>
                    <SearchIcon sx={{ color: "#e04132", cursor: "pointer" }} />
                  </IconButton>
                ),
              }}
              value={dataSearch.keyword}
              onChange={(e) => {
                setDataSearch({ ...dataSearch, keyword: e.target.value });
              }}
              onKeyUp={(e) => (e.code === "Enter" ? onSearchButton() : null)}
            />
          </WrapperSearch>

          <WrapperButton>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{
                minWidth: 180,
                height: 52,
                "@media (max-width: 768px)": {
                  flex: 1,
                },
              }}
            >
              เพิ่มใบรับรอง
            </Button>
          </WrapperButton>
        </DivMuiInside>

        {/* Results Count */}
        <Box sx={{ mb: 2 }}>
          <Typography component="span" color="textSecondary">
            ใบรับรองทั้งหมด:{" "}
            <Typography
              component="span"
              sx={{ color: "#E13B30", fontWeight: 600 }}
            >
              {rowCount}
            </Typography>{" "}
            รายการ
          </Typography>
        </Box>

        {/* Data Grid */}
        <WrapperDataGrid>
          <DataGrid
            getRowId={(row) => row._id}
            rows={certificatesList}
            columns={deviceWidth > 768 ? DesktopColumns : MobileColumns}
            loading={isLoading}
            pageSize={dataSearch.pageSize}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            disableColumnResize={deviceWidth <= 768}
            getRowHeight={() => (deviceWidth > 768 ? 80 : 120)}
            sx={{
              "& .MuiDataGrid-row": {
                "&:hover": {
                  backgroundColor: "#f9f9f9",
                },
              },
              "& .MuiDataGrid-columnSeparator": {
                display: "none",
              },
            }}
          />
        </WrapperDataGrid>
      </DivMuiContainer>
    </Main>
  );
};

export default CertificatesList;
